import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import './PrivacyPolicy.css';

const PrivacyPolicy: React.FC = () => {
  const navigate = useNavigate();
  const lastUpdated = 'October 2, 2026';

  return (
    <div className="privacy-policy-container">
      <header className="privacy-header">
        <div className="privacy-logo" onClick={() => navigate('/')}>
          <span className="logo-icon">&#9678;</span>
          SiteOperations.io
        </div>
      </header>
      <div className="privacy-policy-content">
        <button className="back-button" onClick={() => navigate(-1)}>
          <ArrowLeft size={20} />
          Back
        </button>

        <h1>Privacy Policy</h1>
        <p className="last-updated">Last Updated: {lastUpdated}</p>

        <section>
          <h2>1. Introduction</h2>
          <p>
            SiteOperations.io is operated by West Clinical Research, Inc., 4218 Arendell Street,
            Suite C, Morehead City, NC 28557 ("we," "our," or "us"). It provides a clinical research operations
            platform used by clinical research sites and site networks to run their day-to-day
            work: tracking patient visits, recruiting and communicating with study participants,
            verifying investigational product dispensing, managing regulatory documents with
            electronic signatures, reviewing medical records for pre-screening, keeping staff
            training records, negotiating budgets and contracts, answering a 24-hour participant
            phone line, and related tools.
          </p>
          <p>
            This Privacy Policy explains what information the platform collects, how it is used,
            who it is shared with, and the choices available to you. It applies to the platform at
            siteoperations.io and its subdomains, including the pages a research participant
            reaches through a link we text or email on a site's behalf, and the clinician portal
            a site makes available to referring healthcare providers.
          </p>
          <p>
            By accessing or using SiteOperations.io, you agree to the terms of this Privacy
            Policy. If you do not agree with the terms, please do not access the platform.
          </p>
        </section>

        <section>
          <h2>2. Our Role and the Role of Your Research Site</h2>
          <p>
            Most information in the platform is entered or collected by a clinical research site
            for its own operations. In those cases the research site decides what is collected and
            why, and we process it on the site's behalf as a service provider and, where the Health
            Insurance Portability and Accountability Act (HIPAA) applies, as a business associate
            under a Business Associate Agreement with the site.
          </p>
          <p>
            If you are a research participant, a prospective participant, or a patient referred
            to a study by your clinician, the research site is responsible for your information.
            The site's Notice of Privacy Practices and the informed consent you signed describe how
            your information is used and the rights you have. Please contact the site to exercise
            those rights; we support the site in responding to your request.
          </p>
          <p>
            Information about platform user accounts, such as names, work email addresses,
            sign-in data, and usage logs, is processed by us in order to operate the platform.
          </p>
        </section>

        <section>
          <h2>3. Information We Collect</h2>

          <h3>3.1 Account Information</h3>
          <p>When a site staff member or a clinician creates an account or signs in, we collect:</p>
          <ul>
            <li>Full name, work email address, role, and site or network assignments</li>
            <li>
              Sign-in credentials: a password, which our identity provider stores only as a hash,
              or your Google account profile if you choose to sign in with Google; the clinician
              portal signs in with a one-time code sent to your email or phone
            </li>
            <li>Notification preferences and push notification registration for your device</li>
            <li>
              Electronic signature records: your name, the date and time, the meaning of the
              signature, and a password re-check at the moment of signing, as required by
              21 CFR Part 11
            </li>
          </ul>

          <h3>3.2 Participant and Prospective-Participant Information</h3>
          <p>
            When a site uses the recruitment tools, we collect information about people who
            express interest in a study or are referred to one:
          </p>
          <ul>
            <li>Name, phone number, email address, date of birth, and location</li>
            <li>Pre-screening questionnaire answers, which can include health information</li>
            <li>
              Text messages and emails exchanged with the site, transcripts of phone calls with
              the site, voicemails, and callback bookings
            </li>
            <li>Documents the site shares with you and documents you sign electronically</li>
            <li>Your consent choices and opt-out status</li>
            <li>
              Referral information a clinician submits through the clinician portal, with your
              consent
            </li>
            <li>
              Where a site runs online advertising, the campaign and click identifiers attached to
              the link you arrived from
            </li>
          </ul>

          <h3>3.3 Visit Tracking and Operations Data</h3>
          <ul>
            <li>
              Patient initials and subject IDs, appointment times, visit and study names, visit
              status, room assignments, staff assignments, and operational notes
            </li>
            <li>Study and protocol organization, team membership, bookmarks, links, and contacts</li>
            <li>Workload and timing analytics derived from visit history</li>
          </ul>

          <h3>3.4 Protected Health Information in Regulated Workflows</h3>
          <p>
            Some tools handle full identifiers and medical information by design, with the
            safeguards described in Section 7:
          </p>
          <ul>
            <li>
              Medical records a site uploads for pre-screening review and the medical histories
              extracted from them (Medical History Review)
            </li>
            <li>
              Subject dates of birth, photographs of kit-assignment documents and kit labels, and
              dispensing verification reports (IXVerify)
            </li>
            <li>
              Regulatory documents, delegation logs, training records, and electronic signatures
              (eReg)
            </li>
            <li>
              The caller's phone number, call transcripts, and voicemails for calls to a site's
              24-hour line; a notice that the call is transcribed is played at the start of each
              call (24-Hour Line)
            </li>
          </ul>

          <h3>3.5 Training and Compliance Records</h3>
          <ul>
            <li>
              Course progress, quiz attempts, completions, certificates, competency sign-offs,
              external certificates, waivers, and onboarding checklist items (Learning Center)
            </li>
          </ul>

          <h3>3.6 Business Documents</h3>
          <ul>
            <li>
              Sponsor budgets, agreements, and payment information a site uploads for negotiation
              (Budgets and Contracts); these may name individual sponsor, CRO, or site staff
            </li>
          </ul>

          <h3>3.7 Usage Information</h3>
          <p>We automatically collect:</p>
          <ul>
            <li>Log data: IP address, browser and device type, pages and features used, access times</li>
            <li>
              Audit trail entries recording who viewed or changed what, and when; several of these
              trails are append-only and cannot be edited or deleted by anyone
            </li>
            <li>Error reports and performance data</li>
          </ul>

          <h3>3.8 Information From Your Site's Other Systems</h3>
          <ul>
            <li>Appointment exports from the site's scheduling and reporting systems</li>
            <li>
              Subject, visit, and scheduling data exchanged with the site's clinical trial
              management system
            </li>
          </ul>
        </section>

        <section>
          <h2>4. How We Use Your Information</h2>
          <p>We use the information we collect to:</p>
          <ul>
            <li>Provide, maintain, secure, and improve the platform</li>
            <li>Authenticate users and enforce roles and per-tool access</li>
            <li>Show real-time visit status and send notifications in the app, by push, email, or text</li>
            <li>
              Communicate with prospective participants on a site's behalf and schedule callbacks
              and visits
            </li>
            <li>Read, extract, summarize, and verify information in documents (see Section 5)</li>
            <li>
              Create regulatory records: audit trails, electronic signatures, verification reports,
              and training certificates
            </li>
            <li>Generate operational reports and analytics for the site</li>
            <li>Measure the effectiveness of a site's recruitment advertising (see Section 6)</li>
            <li>
              Detect, prevent, and investigate security incidents, abuse, and misuse, and enforce
              rate and spending limits
            </li>
            <li>Comply with legal obligations and respond to lawful requests</li>
          </ul>
          <p>
            We do not use your information to train artificial intelligence models, we do not sell
            it, and we do not use it to advertise to you.
          </p>
        </section>

        <section>
          <h2>5. Artificial Intelligence</h2>
          <p>Several tools use AI models to do work that site staff then review:</p>
          <ul>
            <li>
              Reading uploaded documents (optical character recognition) and extracting and
              summarizing medical history from medical records
            </li>
            <li>
              Reading kit-assignment documents and kit labels during dispensing verification; the
              comparison of kit numbers is made by program code, not by the model, and a person
              attests the result
            </li>
            <li>Answering staff questions about a site's own documents</li>
            <li>
              Giving a preliminary read of pre-screening answers against study criteria for a
              recruiter to review
            </li>
            <li>
              Drafting training courses and quizzes from material an administrator uploads, which
              a person reads and publishes
            </li>
            <li>
              Carrying on text-message conversations with prospective participants on a site's
              behalf, within rules the site sets; you can ask for a person at any time and reply
              STOP to end messages
            </li>
            <li>Transcribing phone calls and voicemails</li>
          </ul>
          <p>
            AI output is a draft or an aid. Decisions about eligibility, enrollment, dispensing,
            and care are made by people at the site. We use AI models from Amazon Web Services
            (Amazon Bedrock), Google Cloud, and Anthropic under commercial terms that do not permit
            the provider to use your data to train its models. Protected Health Information is
            processed only through HIPAA-eligible services at Amazon Web Services and Google Cloud;
            the Anthropic API is used only to draft training content and receives no participant or
            patient information. Every AI call is metered and subject to spending limits.
          </p>
        </section>

        <section>
          <h2>6. Data Sharing and Disclosure</h2>
          <p>
            We do not sell your personal information, and we do not share it for cross-context
            behavioral advertising. We share information with:
          </p>

          <h3>6.1 Service Providers</h3>
          <p>We use third-party services to operate the platform, each for the purpose named:</p>
          <ul>
            <li>
              <strong>Google Cloud:</strong> application hosting, databases, file storage, document
              OCR, and data exchange with a site's clinical trial management system
            </li>
            <li>
              <strong>Amazon Web Services:</strong> AI models (Amazon Bedrock), document OCR
              (Textract), and file storage (S3)
            </li>
            <li>
              <strong>Supabase:</strong> user authentication (sign-in and session management)
              only; no platform data is stored there
            </li>
            <li><strong>Google:</strong> sign in with Google, if you choose it</li>
            <li>
              <strong>Anthropic:</strong> AI model for drafting training content; receives no
              patient or participant information
            </li>
            <li>
              <strong>Twilio:</strong> text messages, phone calls, call transcription, and the
              24-hour line
            </li>
            <li><strong>Telnyx:</strong> fax transmission of records to and from clinicians' offices</li>
            <li><strong>Resend:</strong> transactional email</li>
            <li><strong>OneSignal:</strong> push notifications</li>
            <li>
              <strong>Meta Platforms:</strong> when a research site advertises on Facebook or
              Instagram, we send Meta a limited set of events (a lead was captured; a registration
              was completed) with a hashed email address or phone number and the identifiers from
              the ad click, so the site can measure which ads led to sign-ups. No name, health
              information, study name, or questionnaire answer is sent. Hashing is pseudonymization,
              not de-identification, and a site enables this only with documented consent.
            </li>
          </ul>
          <p>
            A site's own scheduling, reporting, and clinical trial management systems exchange data
            with the platform at the site's direction and under the site's agreements with those
            vendors.
          </p>

          <h3>6.2 Within Your Organization</h3>
          <p>
            Information is shared with other authorized users within your clinical site or network
            according to their role and tool access, as necessary for operational purposes.
          </p>

          <h3>6.3 Sponsors, CROs, Monitors, Auditors, and Regulators</h3>
          <p>
            At a site's direction, the platform produces and delivers records the site is required
            to share: dispensing verification reports emailed to the people the site names,
            regulatory documents and signatures, and training certificates.
          </p>

          <h3>6.4 Referring Clinicians</h3>
          <p>
            With a referred patient's consent, a site may share the status of the referral with the
            clinician who made it.
          </p>

          <h3>6.5 Legal Requirements</h3>
          <p>
            We may disclose information if required by law, regulation, legal process, or
            governmental request, or to protect the rights, property, or safety of a site, its
            participants, our users, or the public.
          </p>

          <h3>6.6 Business Transfers</h3>
          <p>
            If we are involved in a merger, acquisition, or sale of assets, your information may
            be transferred as part of that transaction and will remain subject to this Privacy
            Policy.
          </p>
        </section>

        <section>
          <h2>7. Data Security</h2>
          <p>
            We implement technical and organizational security measures appropriate to the
            sensitivity of the information, including:
          </p>
          <ul>
            <li>Encryption of data in transit (TLS) and at rest on every database and storage bucket</li>
            <li>
              Additional field-level encryption (AES-256-GCM with per-site or per-network keys)
              for medical record content, subject dates of birth, callers' phone numbers, and call
              transcripts
            </li>
            <li>Databases on private networks that are not reachable from the Internet</li>
            <li>
              Role-based access and per-tool entitlements checked on every request, and audit
              logging of access to Protected Health Information
            </li>
            <li>Password re-authentication for electronic signatures and countersignatures</li>
            <li>
              Append-only audit trails, and cryptographic hashes recorded for every evidence file
              and report so that later alteration is detectable
            </li>
            <li>
              Rate limits and spending caps on AI features, and signature verification on every
              message our systems accept from a telephony or email provider
            </li>
            <li>Automated security tests that run before each deployment</li>
          </ul>
          <p>
            No method of transmission over the Internet or electronic storage is completely secure.
            If we learn of a breach affecting your information, we will notify the affected site
            and, where the law requires it, you and the relevant regulators.
          </p>
        </section>

        <section>
          <h2>8. Where Your Information Is Stored</h2>
          <p>
            The platform is hosted in the United States, on Google Cloud in South Carolina and on
            Amazon Web Services in Northern Virginia. Our service providers process information in
            the United States unless noted otherwise in their own terms.
          </p>
        </section>

        <section>
          <h2>9. Data Retention</h2>
          <ul>
            <li>
              <strong>Regulatory records</strong> (regulatory documents, electronic signatures,
              delegation logs, dispensing verification reports and their evidence, audit trails, and
              training certificates) are retained for as long as the site's regulatory obligations
              require, commonly for years after a study closes. Append-only records cannot be
              altered or deleted.
            </li>
            <li>
              <strong>Participant recruitment records</strong>, including messages, call transcripts,
              voicemails, and medical records reviewed for pre-screening, are retained until the
              site directs otherwise.
            </li>
            <li>
              <strong>Visit tracking history</strong> is retained to support the site's operational
              analytics.
            </li>
            <li>
              <strong>Account information</strong> is retained while your account is active. A
              deactivated account is kept in the records that reference it, such as signatures,
              audit trails, and staff assignments, so that those records remain accurate.
            </li>
            <li>
              <strong>Usage logs</strong> are retained for a limited period for security and
              troubleshooting.
            </li>
          </ul>
          <p>
            When you or your site ask us to delete personal information, we delete or de-identify it
            within 30 days unless it forms part of a regulatory record or is needed to meet a legal
            obligation, in which case we tell you what is being kept and why.
          </p>
        </section>

        <section>
          <h2>10. Your Choices and Rights</h2>
          <ul>
            <li>
              <strong>Platform users:</strong> you can view and update your name and preferences in
              the app, and ask your site administrator or us for access to, correction of, deletion
              of, or a copy of your information.
            </li>
            <li>
              <strong>Participants and prospective participants:</strong> contact your research site.
              Your rights under HIPAA and under your informed consent are exercised with the site,
              and we support the site's response.
            </li>
            <li>
              <strong>Text messages:</strong> reply STOP at any time to stop receiving messages and
              START to resume. You can ask for a person at any time.
            </li>
            <li>
              <strong>Push notifications:</strong> turn them off in the app or in your device
              settings.
            </li>
            <li>
              <strong>Email:</strong> service and regulatory emails are necessary to operate the
              platform; optional reminders and digests can be adjusted in your notification
              preferences.
            </li>
          </ul>
          <p>
            Depending on where you live, you may also have the right to know what personal
            information we hold about you, to correct or delete it, to receive a copy in a portable
            form, to object to or restrict certain processing, to withdraw consent where processing
            is based on consent, and not to be discriminated against for exercising these rights.
            We do not sell personal information or share it for targeted advertising, so there is
            nothing to opt out of in that respect. To exercise these rights, contact us using the
            information in Section 15; we will verify your request and respond within the time the
            applicable law allows.
          </p>
        </section>

        <section>
          <h2>11. HIPAA and 21 CFR Part 11</h2>
          <p>
            When a site uses the platform to handle Protected Health Information, we act as the
            site's business associate. We use and disclose that information only to provide the
            services, as permitted by our Business Associate Agreement with the site and by the
            HIPAA Privacy and Security Rules. A Business Associate Agreement is available to any
            site on request.
          </p>
          <p>
            The platform provides electronic signatures, append-only audit trails, and record
            integrity controls designed to support 21 CFR Part 11 compliance. Each site remains
            responsible for its own compliance program, procedures, and validation.
          </p>
        </section>

        <section>
          <h2>12. Cookies, Local Storage, and Tracking</h2>
          <p>
            The platform keeps your session token and preferences in your browser's local storage.
            The clinician portal uses essential cookies for its sign-in session, to recognize a
            device you have signed in from before, and to complete the sign-in flow. We do not use
            third-party advertising or analytics cookies on the platform, and the participant-facing
            pages we host (questionnaires, callback booking, and electronic signing) carry no
            advertising pixels or trackers.
          </p>
          <p>
            A research site's own public website is separate from the platform and is governed by
            that site's privacy policy, which may describe advertising or analytics tools the site
            uses there. Because we do not track you across websites, we do not change our behavior
            in response to browser "Do Not Track" signals.
          </p>
        </section>

        <section>
          <h2>13. Children's Privacy</h2>
          <p>
            Platform accounts are for adults 18 years of age and older, and we do not knowingly
            create accounts for children. Information about a minor who is screened for or enrolled
            in a study is collected only by the research site, through its own informed consent and
            assent process with a parent or legal guardian.
          </p>
        </section>

        <section>
          <h2>14. Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. We will notify you of any material
            changes by posting the new policy on this page and updating the "Last Updated" date, and
            we will tell the sites we serve about changes that affect how their information is
            handled.
          </p>
        </section>

        <section>
          <h2>15. Contact Us</h2>
          <p>
            If you have questions about this Privacy Policy or our data practices, please contact
            us at:
          </p>
          <p className="contact-info">
            <strong>West Clinical Research, Inc.</strong><br />
            4218 Arendell Street, Suite C<br />
            Morehead City, NC 28557<br />
            <strong>Email:</strong> info@westclinicalresearch.com<br />
            <strong>Website:</strong> https://siteoperations.io
          </p>
          <p>
            If you are a research participant with a question about your own information, please
            also contact the research site you are working with; the site holds your consent and is
            best placed to help.
          </p>
        </section>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
